/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React from "react";
import { Autocomplete } from "@material-ui/lab";
import { TextField, CircularProgress } from "@material-ui/core";
import useEffectAsync from "lib/react/customHooks";
import { getCompanies } from "lib/services/companies";

// Sentinel option that represents "no company" (company_id === null).
const NO_COMPANY = { id: null, name: "— No company —" };

// The company list is shared across every autocomplete instance and barely
// changes, so cache it module-wide. Otherwise each row you open re-downloads
// the whole list, which is a big chunk of the sluggishness while editing.
// (Cleared on a full page reload; add-company flows are rare enough that the
// small staleness window is acceptable.)
let companiesCache = null;

export default function CompaniesAutocomplete({
  selectedCompanyId,
  onChange,
  inputProps = {},
  allowEmpty = false
}) {
  const [companies, setCompanies] = React.useState(() => companiesCache || []);
  const [value, setValue] = React.useState(() => {
    if (companiesCache && selectedCompanyId) {
      return (
        companiesCache.find(company => company.id === selectedCompanyId) || null
      );
    }
    return allowEmpty && !selectedCompanyId ? NO_COMPANY : null;
  });
  const [loading, setLoading] = React.useState(!companiesCache);

  // Fetch the company list once (then reuse the cache) so we can (a) preselect
  // the row's current company as the value and (b) offer every option
  // immediately, instead of leaving the field blank until the dropdown opens.
  useEffectAsync(
    async () => {
      if (companiesCache) return companiesCache;
      const result = await getCompanies();
      companiesCache = result.data;
      return companiesCache;
    },
    data => {
      setCompanies(data);
      const current = data.find(company => company.id === selectedCompanyId);
      if (current) setValue(current);
      setLoading(false);
    },
    []
  );

  const options = allowEmpty ? [NO_COMPANY, ...companies] : companies;

  const handleChange = (_event, newValue) => {
    // A truthy option with a real id is a company; the sentinel or a cleared
    // field both mean "no company" and are reported to the parent as null.
    const selected = newValue && newValue.id != null ? newValue : null;
    setValue(newValue || (allowEmpty ? NO_COMPANY : null));
    if (onChange) onChange(selected);
  };

  return (
    <Autocomplete
      options={options}
      value={value}
      getOptionLabel={option => option.name}
      getOptionSelected={(option, selected) => option.id === selected.id}
      onChange={handleChange}
      loading={loading}
      renderInput={params => {
        const props = { ...params, ...inputProps };
        return (
          <TextField
            {...props}
            placeholder="Companies"
            InputProps={{
              ...params.InputProps,
              ...inputProps.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        );
      }}
    />
  );
}
